import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'

import ModalContainer from '../../../ModalContainer/ModelContainer'
import TextCell from '../components/TextCell/TextCell'
import FeaturedTable from '../../../FeaturedTable/FeaturedTable'
import ConfirmPopup from '../../../Popups/ConfirmPopup'
import Icon from '../../../Icons'
import { useSnackbar } from '../../../../helpers/hooks/useSnackbar'
import RewardsActivityEvidenceModal from '../RewardsActivityEvidenceModal/RewardsActivityEvidenceModal'
import { ZoomFrame } from '@/components/ui/zoomable-image'

import { getAssetLink } from '../../../../helpers/get-asset-link'
import { loyaltyProfileColumns, renderEvidence } from '../helpers'
import api from '../../../../api/api'

const RewardsLoyaltyProfileModal = ({
  onClose = () => {},
  onAddActivity = () => {},
  setActivityDetails = () => {},
  client,
  building,
}) => {
  const { t } = useTranslation(['rewards-loyalty', 'common'])
  const [isLoading, setIsLoading] = useState(true)
  const [profileData, setProfileData] = useState(null)
  const [deleteActivity, setDeleteActivity] = useState(null)
  const [reviewActivity, setReviewActivity] = useState(null)
  const [showApproveDialog, setShowApproveDialog] = useState(false)
  const [showDeclineDialog, setShowDeclineDialog] = useState(false)
  const [photoIndexToDisplay, setPhotoIndexToDisplay] = useState(null)

  const onOpenImageModal = (index) => setPhotoIndexToDisplay(index)
  const { displaySnackbar } = useSnackbar()
  const clientId = client.clientId || client.id

  const { timezone } = building

  const fetchLoyaltyProfile = async () => {
    try {
      setIsLoading(true)
      const { data } = await api.fetchLoyaltyProfile(clientId)
      setProfileData(data)
    } catch ({ message }) {
      displaySnackbar(message, true)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    if (client) {
      fetchLoyaltyProfile()
    }
  }, [client, building])

  const onDeleteActivity = async () => {
    try {
      await api.deleteLoyaltyClientActivity(deleteActivity.id, clientId)
      displaySnackbar(
        t('rewards-loyalty:loyaltyProfile.deleteActivitySuccess', { name: deleteActivity.name }),
      )
    } catch {
      displaySnackbar(t('rewards-loyalty:loyaltyProfile.deleteActivityError'), true)
    } finally {
      fetchLoyaltyProfile()
      setDeleteActivity(null)
    }
  }

  const onCompleteActivity = async (activity) => {
    try {
      await api.completeLoyaltyClientActivity(activity.id, clientId)
    } catch ({ message }) {
      displaySnackbar(message, true)
    } finally {
      fetchLoyaltyProfile()
    }
  }

  const onApproveActivity = async () => {
    try {
      await api.loyaltyApproveActivity(reviewActivity.historyId || reviewActivity.id)
      displaySnackbar(
        t('rewards-loyalty:loyaltyTable.pointsApprovedSuccess', {
          points: reviewActivity.points,
          activityName: reviewActivity.name,
          name: reviewActivity.residentName,
        }),
      )
      fetchLoyaltyProfile()
    } catch {
      displaySnackbar(t('rewards-loyalty:loyaltyTable.activityApprovedError'), true)
    } finally {
      setShowApproveDialog(false)
      setReviewActivity(null)
    }
  }

  const onDeclineActivity = async () => {
    try {
      await api.loyaltyDeclineActivity(reviewActivity.historyId || reviewActivity.id)
      displaySnackbar(t('rewards-loyalty:loyaltyTable.activityDeclinedSuccess'))
      fetchLoyaltyProfile()
    } catch {
      displaySnackbar(t('rewards-loyalty:loyaltyTable.activityDeclinedError'), true)
    } finally {
      setShowDeclineDialog(false)
      setReviewActivity(null)
    }
  }

  const cautionActivityBody = () => {
    const { name, residentName } = reviewActivity
    const text = showApproveDialog
      ? t('rewards-loyalty:loyaltyTable.approveActivityConfirm', {
          activityName: name,
          name: residentName,
        })
      : t('rewards-loyalty:loyaltyTable.declineActivityConfirm', {
          name: residentName,
          activityName: name,
        })

    return (
      <div className="caution-activity-body">
        <span>{text}</span>
        <div className="caution-activity-evidence">
          <span>{t('rewards-loyalty:evidence.activityCompletionEvidence')}</span>
          {renderEvidence(reviewActivity, onOpenImageModal, photoIndexToDisplay, t)}
        </div>
      </div>
    )
  }

  return (
    <React.Fragment>
      <ModalContainer
        additionalClass={'rewards-modal'}
        onClose={onClose}
        headerTitle={t('rewards-loyalty:loyaltyProfile.headerTitle')}
        headerIcon={<Icon icon="diamondStar" />}
      >
        <div
          className={`rewards-modal-content rewards-base-view rewards-loyalty ${
            isLoading ? 'is-loading' : ''
          }`}
        >
          <div className="rewards-loyalty-head">
            <div>
              <ZoomFrame
                className="zoom-avatar-frame"
                hintSize="2rem"
                src={getAssetLink({ path: 'chitown-avatar/avatar', file: profileData?.avatar })}
                title={profileData?.clientName ?? ''}
              >
                <img
                  src={getAssetLink({ path: 'chitown-avatar/avatar', file: profileData?.avatar })}
                />
              </ZoomFrame>
              <TextCell
                title={
                  profileData
                    ? `${profileData.clientName} ${
                        profileData.clientApartment ? `#${profileData.clientApartment}` : ''
                      }`
                    : ''
                }
                text={profileData ? profileData.clientEmail : ''}
              />
              <TextCell
                title={t('rewards-loyalty:loyaltyProfile.completedActivitiesLabel')}
                text={
                  profileData
                    ? `${profileData.completedActivitiesCount || 0}/${
                        profileData.activities.length
                      }`
                    : '0/0'
                }
              />
              <TextCell
                title={t('rewards-loyalty:mapDetails.pointsEarned')}
                text={profileData && `${profileData.pointsEarned || 0}`}
              />
              <TextCell
                title={t('rewards-loyalty:loyaltyProfile.currentBalanceLabel')}
                text={(profileData && profileData.balance) || '$0'}
              />
            </div>
            <div>
              <button className="button button-black" onClick={onAddActivity} disabled={isLoading}>
                {t('rewards-loyalty:loyaltyTable.addActivityButton')}
              </button>
            </div>
          </div>
          {!isLoading && profileData && (
            <div className="rewards-table" style={{ padding: 0 }}>
              <div className={`ctp-rewards-loyalty-list`}>
                <FeaturedTable
                  columns={loyaltyProfileColumns({
                    timezone,
                    onReview: (activity) =>
                      setReviewActivity({ ...activity, residentName: profileData.clientName }),
                    onDetails: (activity) =>
                      setActivityDetails({ ...activity, residentName: profileData.clientName }),
                    onComplete: onCompleteActivity,
                    onDelete: setDeleteActivity,
                    t,
                  })}
                  data={profileData.activities}
                />
              </div>
            </div>
          )}
        </div>
      </ModalContainer>
      <ConfirmPopup
        isOpen={!!deleteActivity}
        cancelCallback={() => setDeleteActivity(null)}
        confirmCallback={onDeleteActivity}
        cancelText={t('common:actions.cancel')}
        confirmText={t('common:actions.delete')}
        title={t('rewards-loyalty:loyaltyTable.confirmationTitle')}
        body={t('rewards-loyalty:loyaltyProfile.deleteActivityConfirm')}
      />
      {reviewActivity && (
        <RewardsActivityEvidenceModal
          activity={reviewActivity}
          onClose={() => setReviewActivity(null)}
          onApprove={() => setShowApproveDialog(true)}
          onDecline={() => setShowDeclineDialog(true)}
        />
      )}
      {showApproveDialog && (
        <ConfirmPopup
          title={t('rewards-loyalty:loyaltyTable.cautionTitle')}
          cancelText={t('rewards-loyalty:loyaltyTable.closeButton')}
          confirmText={t('rewards-loyalty:loyaltyTable.approveButton')}
          isOpen={showApproveDialog}
          body={cautionActivityBody()}
          cancelCallback={() => setShowApproveDialog(false)}
          confirmCallback={onApproveActivity}
          className={'is-caution'}
        />
      )}
      {showDeclineDialog && (
        <ConfirmPopup
          title={t('rewards-loyalty:loyaltyTable.cautionTitle')}
          cancelText={t('rewards-loyalty:loyaltyTable.closeButton')}
          confirmText={t('rewards-loyalty:loyaltyTable.declineButton')}
          isOpen={showDeclineDialog}
          body={cautionActivityBody()}
          cancelCallback={() => setShowDeclineDialog(false)}
          confirmCallback={onDeclineActivity}
          className={'is-caution'}
        />
      )}
    </React.Fragment>
  )
}

RewardsLoyaltyProfileModal.propTypes = {
  onClose: PropTypes.func,
  onAddActivity: PropTypes.func,
  setActivityDetails: PropTypes.func,
  client: PropTypes.object,
  building: PropTypes.object,
}

export default RewardsLoyaltyProfileModal
